import { useState } from "react";
import { useNavigate } from "@/lib/router-compat";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ShieldCheck, Calendar, FileText } from "lucide-react";
import { postTrackingEvent, buildTrackingPayload } from "@/lib/tracking";
import { trackLead } from "@/lib/ga4";
import { trackLeadConversion } from "@/lib/conversions";

interface EmployerInquiryFormProps {
  /** Where to redirect after submit. */
  redirectTo?: string;
}

// Registered custom field IDs (from register_custom_field)
const CF_TITLE = "qvt75wNK8wARN7AvtdaY";
const CF_EMPLOYEE_COUNT = "qvYeKToAbVugIwf6mfrH";
const CF_ROLE = "5nLegyBWeADnyy3pmCED";
const CF_INSURANCE = "itVzu3EI1s0YtPynk7gk";
const CF_INTEREST = "Hs7AUGpT5ZYlzCgK55Yu";
const CF_TIMELINE = "LbYgV42cPUAGYgdLvBMg";
const CF_HELP = "OzjXZT8yjNsoUfzda1bN";
const CF_AFFECTED = "4WxGpPY8Tjyfsx5zXAf6";

/**
 * Employer / HR inquiry form for /saint-employers.
 * Captures executive/benefits-manager info and routes to the CRM
 * with a suggested lead tier. Redirects to thank-you page on submit.
 */
export const EmployerInquiryForm: React.FC<EmployerInquiryFormProps> = ({
  redirectTo = "/thank-you-saint-google",
}) => {
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [title, setTitle] = useState("");
  const [company, setCompany] = useState("");
  const [employeeCount, setEmployeeCount] = useState("");
  const [role, setRole] = useState("");
  const [insurance, setInsurance] = useState("");
  const [interest, setInterest] = useState("");
  const [timeline, setTimeline] = useState("");
  const [affected, setAffected] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    const source = "saint-employers";
    const payload = buildTrackingPayload(
      source,
      "SAINT Employer Inquiry — saint-employers",
    );

    // Standard CRM fields
    payload.formData = {
      first_name: firstName,
      last_name: lastName,
      email,
      phone,
      organization: company,
    };
    payload.formLabels = {
      first_name: "First name",
      last_name: "Last name",
      email: "Email",
      phone: "Phone",
      organization: "Employer / Company name",
    };

    // Custom fields
    const customFields = {
      [CF_TITLE]: { value: title, label: "Title" },
      [CF_EMPLOYEE_COUNT]: {
        value: employeeCount,
        label: "Number of Employees",
      },
      [CF_ROLE]: { value: role, label: "Role" },
      [CF_INSURANCE]: { value: insurance, label: "Insurance Carrier" },
      [CF_INTEREST]: { value: interest, label: "Primary Interest" },
      [CF_TIMELINE]: { value: timeline, label: "Timeline" },
      [CF_HELP]: { value: message, label: "How can we help?" },
      [CF_AFFECTED]: { value: affected, label: "Number of Affected Employees" },
    };

    trackLead(source, "SAINT Employer Inquiry — saint-employers");
    trackLeadConversion(source, "SAINT Employer Inquiry — saint-employers");

    try {
      await postTrackingEvent(payload, { customFields });
    } catch {
      /* fire-and-forget — still redirect */
    }
    navigate(redirectTo);
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-6 text-foreground shadow-soft sm:p-8">
      <div className="mb-5">
        <h2 className="font-headings text-xl font-bold text-primary sm:text-2xl">
          Get your team back to work
        </h2>
        <p className="mt-1.5 text-sm text-muted-foreground">
          Tell us about your organization and we'll reach out within one
          business day with next steps, a consultation, and any collateral you
          need.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Name row */}
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="emp-first">First name *</Label>
            <Input
              id="emp-first"
              required
              placeholder="Jordan"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="emp-last">Last name *</Label>
            <Input
              id="emp-last"
              required
              placeholder="Smith"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </div>
        </div>

        {/* Contact row */}
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="emp-email">Work email *</Label>
            <Input
              id="emp-email"
              type="email"
              required
              placeholder="jordan@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="emp-phone">Phone *</Label>
            <Input
              id="emp-phone"
              type="tel"
              required
              placeholder="(555) 123-4567"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>
        </div>

        {/* Title + Company */}
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="emp-title">Title *</Label>
            <Input
              id="emp-title"
              required
              placeholder="VP of Human Resources"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="emp-company">Employer / Company name *</Label>
            <Input
              id="emp-company"
              required
              placeholder="Acme Corporation"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
            />
          </div>
        </div>

        {/* Role + Employee count */}
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="emp-role">Your role *</Label>
            <Select value={role} onValueChange={setRole} required>
              <SelectTrigger id="emp-role">
                <SelectValue placeholder="Select your role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="HR Leader">HR Leader</SelectItem>
                <SelectItem value="Benefits Manager">
                  Benefits Manager
                </SelectItem>
                <SelectItem value="Executive (C-Suite)">
                  Executive (C-Suite)
                </SelectItem>
                <SelectItem value="Occupational Health">
                  Occupational Health
                </SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="emp-count">Number of employees *</Label>
            <Select
              value={employeeCount}
              onValueChange={setEmployeeCount}
              required
            >
              <SelectTrigger id="emp-count">
                <SelectValue placeholder="Select size" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1-50">1–50</SelectItem>
                <SelectItem value="51-200">51–200</SelectItem>
                <SelectItem value="201-500">201–500</SelectItem>
                <SelectItem value="501-1000">501–1,000</SelectItem>
                <SelectItem value="1001-5000">1,001–5,000</SelectItem>
                <SelectItem value="5000+">5,000+</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Affected employees + Insurance */}
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="emp-affected">
              Approx. employees affected by depression/anxiety
            </Label>
            <Select value={affected} onValueChange={setAffected}>
              <SelectTrigger id="emp-affected">
                <SelectValue placeholder="Select range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1-5">1–5</SelectItem>
                <SelectItem value="6-10">6–10</SelectItem>
                <SelectItem value="11-25">11–25</SelectItem>
                <SelectItem value="26-50">26–50</SelectItem>
                <SelectItem value="50+">50+</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="emp-insurance">Insurance carrier (optional)</Label>
            <Input
              id="emp-insurance"
              placeholder="e.g. Blue Cross Blue Shield, Cigna"
              value={insurance}
              onChange={(e) => setInsurance(e.target.value)}
            />
          </div>
        </div>

        {/* Interest + Timeline */}
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="emp-interest">What would you like? *</Label>
            <Select value={interest} onValueChange={setInterest} required>
              <SelectTrigger id="emp-interest">
                <SelectValue placeholder="Select an option" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Schedule a consultation">
                  <span className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-accent" /> Schedule a
                    consultation
                  </span>
                </SelectItem>
                <SelectItem value="Request collateral/information packet">
                  <span className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-accent" /> Request
                    collateral / info packet
                  </span>
                </SelectItem>
                <SelectItem value="Both">
                  <span className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-accent" /> Both
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="emp-timeline">Timeline *</Label>
            <Select value={timeline} onValueChange={setTimeline} required>
              <SelectTrigger id="emp-timeline">
                <SelectValue placeholder="Select timeline" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Immediate (within 30 days)">
                  Immediate (within 30 days)
                </SelectItem>
                <SelectItem value="1-3 months">1–3 months</SelectItem>
                <SelectItem value="3-6 months">3–6 months</SelectItem>
                <SelectItem value="Exploring">Exploring</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Message */}
        <div className="space-y-2">
          <Label htmlFor="emp-message">How can we help? (optional)</Label>
          <Textarea
            id="emp-message"
            rows={3}
            placeholder="Tell us about your workforce mental health needs, questions about SAINT®, or anything else you'd like us to know."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
        </div>

        <Button
          type="submit"
          size="lg"
          disabled={submitting}
          className="w-full bg-gradient-accent text-accent-foreground font-bold shadow-glow hover:opacity-90"
        >
          {submitting ? "Sending…" : "Submit Inquiry"}
        </Button>

        <div className="flex items-start gap-2">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
          <p className="text-xs text-muted-foreground leading-relaxed">
            We will never share your information. A member of our clinical team
            will reach out within one business day.
          </p>
        </div>
      </form>
    </div>
  );
};

export default EmployerInquiryForm;
